import type {
  Me,
  Profile,
  ProfileUpdate,
  RaceApi,
  RaceCategory,
  RacePhase,
  RaceRegistration,
  RaceResultRow,
  RaceResults,
  RaceSummary,
  RideRecord,
  User,
} from "./types";

export interface HttpRaceApiOptions {
  fetchImpl?: typeof fetch;
  /** Same-origin by default. Tests pass an absolute origin. */
  baseUrl?: string;
}

const JSON_HEADERS = { "content-type": "application/json" };

const ERROR_MESSAGES: Record<string, string> = {
  unauthorized: "Sign in to continue.",
  unauthenticated: "Sign in to continue.",
  guest_cannot_race: "Guest riders can free ride, but races require an account.",
  race_full: "This race is full.",
  registration_closed: "Registration has closed for this race.",
  already_registered: "You are already registered for this race.",
  category_ineligible: "Your category is not eligible for this race.",
  race_not_found: "This race could not be found.",
  network_error: "Could not reach Cimara. Check your connection and try again.",
  http_404: "This is not available on this server yet.",
  http_429: "Too many attempts. Wait a minute and try again.",
  http_500: "The server hit an error. Try again in a moment.",
};

/** A typed HTTP failure from the race API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message?: string) {
    super(message && message.trim() ? message : ERROR_MESSAGES[code] ?? `Request failed (${status}).`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.message && error.message !== error.code) return error.message;
    return ERROR_MESSAGES[error.code] ?? error.message;
  }
  if (error instanceof Error) {
    if (error.name === "AbortError") return "Request cancelled.";
    if (error instanceof TypeError) return ERROR_MESSAGES.network_error;
    return error.message;
  }
  return String(error);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function booleanValue(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function dateValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function normalizeCategory(value: unknown): RaceCategory | null {
  if (value === "A" || value === "B" || value === "C" || value === "D") return value;
  if (typeof value === "string") {
    const upper = value.trim().toUpperCase();
    if (upper === "A" || upper === "B" || upper === "C" || upper === "D") return upper;
  }
  return null;
}

export function normalizePhase(value: unknown): RacePhase | null {
  if (
    value === "scheduled" ||
    value === "registration" ||
    value === "pen" ||
    value === "countdown" ||
    value === "racing" ||
    value === "finished"
  ) {
    return value;
  }
  return null;
}

function normalizeUser(raw: Record<string, unknown>): User {
  return {
    id: stringValue(raw.id) ?? "",
    email: stringValue(raw.email),
    name: stringValue(raw.name) ?? stringValue(raw.displayName),
  };
}

function normalizeProfile(raw: Record<string, unknown>): Profile {
  return {
    displayName:
      stringValue(raw.displayName) ?? stringValue(raw.name) ?? stringValue(raw.username),
    weightKg: numberValue(raw.weightKg) ?? numberValue(raw.weight),
    heightCm: numberValue(raw.heightCm) ?? numberValue(raw.height),
    ftpWatts:
      numberValue(raw.ftpWatts) ?? numberValue(raw.ftpW) ?? numberValue(raw.ftp),
    category: normalizeCategory(raw.category) ?? "D",
    racesCompleted: numberValue(raw.racesCompleted) ?? 0,
    trainerDifficultyPercent: numberValue(raw.trainerDifficultyPercent),
    healthDataConsentAt: stringValue(raw.healthDataConsentAt),
  };
}

function hasProfileFields(raw: Record<string, unknown>): boolean {
  return (
    "displayName" in raw ||
    "weightKg" in raw ||
    "heightCm" in raw ||
    "ftpWatts" in raw ||
    "category" in raw
  );
}

export function normalizeMe(raw: unknown): Me {
  const record = asRecord(raw) ?? {};
  const userRecord = asRecord(record.user) ?? record;
  const profileRecord = asRecord(record.profile) ?? (hasProfileFields(record) ? record : null);
  const profile = profileRecord ? normalizeProfile(profileRecord) : null;
  return {
    user: normalizeUser(userRecord),
    profile,
    category: profile?.category ?? normalizeCategory(record.category) ?? "D",
  };
}

function normalizeRegistration(raw: unknown): RaceRegistration | null {
  const record = asRecord(raw);
  if (!record) return null;
  const registrationId =
    stringValue(record.registrationId) ?? stringValue(record.id) ?? stringValue(record.eventId);
  if (!registrationId) return null;
  return {
    registrationId,
    eventId: stringValue(record.eventId) ?? stringValue(record.raceId) ?? "",
    category: normalizeCategory(record.category) ?? "D",
    pen: numberValue(record.pen) ?? 1,
    ...(numberValue(record.riderId) !== null
      ? { riderId: numberValue(record.riderId) ?? undefined }
      : {}),
    ...(stringValue(record.name) !== null ? { name: stringValue(record.name) ?? undefined } : {}),
  };
}

function normalizeRules(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((rule) => (typeof rule === "string" ? rule.trim() : ""))
      .filter((rule) => rule.length > 0);
  }
  if (typeof value === "string" && value.trim() !== "") {
    return value
      .split(/\r?\n/)
      .map((rule) => rule.trim())
      .filter((rule) => rule.length > 0);
  }
  const record = asRecord(value);
  if (!record) return [];
  const rules: string[] = [];
  if (typeof record.categoryPolicy === "string" && record.categoryPolicy.trim() !== "") {
    rules.push(`Category policy: ${record.categoryPolicy}`);
  }
  if (record.racingUpAllowed === true) rules.push("Racing up a category is allowed.");
  if (record.botsExcludedFromHumanResults === true) {
    rules.push("Bots are excluded from human results.");
  }
  if (numberValue(record.reconnectGraceSeconds) !== null) {
    rules.push(`Reconnect within ${numberValue(record.reconnectGraceSeconds)} seconds or DNF.`);
  }
  return rules;
}

export function normalizeRace(raw: unknown): RaceSummary {
  const record = asRecord(raw) ?? {};
  const route = asRecord(record.route);
  const categoriesRaw = Array.isArray(record.categories)
    ? record.categories
    : typeof record.categories === "string"
      ? record.categories.split(",")
      : [];
  const categories = categoriesRaw
    .map(normalizeCategory)
    .filter((category): category is RaceCategory => category !== null);
  const phase = normalizePhase(record.phase) ?? "scheduled";
  const registration = normalizeRegistration(record.registration);
  const startAt =
    dateValue(record.startAt) ?? dateValue(record.startTime) ?? dateValue(record.startsAt) ?? 0;
  const routeId =
    numberValue(record.routeId) ?? numberValue(route?.id) ?? numberValue(record.route) ?? 0;
  const registered =
    numberValue(record.registered) ??
    numberValue(record.registeredCount) ??
    numberValue(record.registrationCount) ??
    0;
  const registrationOpen =
    booleanValue(record.registrationOpen) ??
    booleanValue(record.isRegistrationOpen) ??
    phase === "registration";
  const isRegistered =
    booleanValue(record.isRegistered) ??
    booleanValue(record.registeredSelf) ??
    registration !== null;

  return {
    id: stringValue(record.id) ?? stringValue(record.eventId) ?? "",
    name: stringValue(record.name) ?? stringValue(record.title) ?? "Race",
    routeId,
    routeName:
      stringValue(record.routeName) ?? stringValue(route?.name) ?? "Cimara route",
    laps: Math.max(1, Math.round(numberValue(record.laps) ?? 1)),
    startAt,
    phase,
    categories,
    capacity: Math.max(0, Math.round(numberValue(record.capacity) ?? 100)),
    registered: Math.max(0, Math.round(registered)),
    botCount: Math.max(0, Math.round(numberValue(record.botCount) ?? 0)),
    mode: stringValue(record.mode) ?? "instanced",
    distanceM:
      numberValue(record.distanceM) ??
      numberValue(record.finishDistanceM) ??
      numberValue(record.routeDistanceM) ??
      numberValue(route?.distanceM),
    description: stringValue(record.description),
    rules: normalizeRules(record.rules),
    registrationOpen,
    registration,
    isRegistered,
    startEdge: numberValue(record.startEdge),
    startS: numberValue(record.startS),
  };
}

function normalizeResultRow(raw: unknown): RaceResultRow {
  const record = asRecord(raw) ?? {};
  const statusValue = stringValue(record.status);
  const status =
    statusValue === "finished" ||
    statusValue === "dnf" ||
    statusValue === "racing" ||
    statusValue === "registered"
      ? statusValue
      : null;
  return {
    position: numberValue(record.position),
    riderId: numberValue(record.riderId) ?? 0,
    userId: stringValue(record.userId),
    name: stringValue(record.name) ?? stringValue(record.displayName) ?? "Rider",
    category: normalizeCategory(record.category) ?? "D",
    timeMs: numberValue(record.timeMs) ?? numberValue(record.time),
    gapMs: numberValue(record.gapMs) ?? numberValue(record.gap),
    avgPower:
      numberValue(record.avgPower) ??
      numberValue(record.averagePower) ??
      numberValue(record.power),
    status,
  };
}

export function normalizeRaceResults(raw: unknown): RaceResults {
  const record = asRecord(raw) ?? {};
  const humansRaw = Array.isArray(record.humans)
    ? record.humans
    : Array.isArray(record.results)
      ? record.results
      : [];
  const pacersRaw = Array.isArray(record.pacers) ? record.pacers : [];
  return {
    eventId: stringValue(record.eventId) ?? stringValue(record.id) ?? "",
    phase: normalizePhase(record.phase) ?? "finished",
    humans: humansRaw.map(normalizeResultRow),
    pacers: pacersRaw.map(normalizeResultRow),
  };
}

function normalizeRide(raw: unknown, index: number): RideRecord {
  const record = asRecord(raw) ?? {};
  const event = asRecord(record.event);
  const result =
    asRecord(record.result) ??
    asRecord(record.raceResult) ??
    asRecord(record.race);
  const resultTimeSeconds = numberValue(result?.timeSeconds);
  const resultGapSeconds = numberValue(result?.gapSeconds);
  const startedAt =
    dateValue(record.startedAt) ?? dateValue(record.startAt) ?? dateValue(record.createdAt);
  const endedAt = dateValue(record.endedAt) ?? dateValue(record.endAt);
  const durationSeconds = numberValue(record.durationSeconds);
  const durationMs =
    numberValue(record.durationMs) ??
    numberValue(record.elapsedMs) ??
    numberValue(record.duration) ??
    (durationSeconds !== null ? durationSeconds * 1000 : null) ??
    (startedAt !== null && endedAt !== null ? Math.max(0, endedAt - startedAt) : 0);
  const eventId =
    stringValue(record.eventId) ??
    stringValue(event?.id) ??
    stringValue(record.raceEventId) ??
    stringValue(record.raceId) ??
    stringValue(result?.eventId) ??
    stringValue(result?.id);
  return {
    id: stringValue(record.id) ?? `ride-${startedAt ?? index}`,
    startedAt,
    endedAt,
    distanceM: numberValue(record.distanceM) ?? numberValue(record.distance) ?? 0,
    durationMs,
    averagePower:
      numberValue(record.averagePower) ??
      numberValue(record.avgPower) ??
      numberValue(record.avgPowerWatts) ??
      numberValue(record.power),
    worldVersion: stringValue(record.worldVersion),
    eventId,
    raceName:
      stringValue(record.raceName) ??
      stringValue(record.eventName) ??
      stringValue(event?.name) ??
      stringValue(result?.name),
    racePosition:
      numberValue(record.racePosition) ??
      numberValue(record.position) ??
      numberValue(result?.position),
    raceCategory:
      normalizeCategory(record.raceCategory) ??
      normalizeCategory(record.category) ??
      normalizeCategory(result?.category),
    raceStatus:
      stringValue(record.raceStatus) ??
      stringValue(record.status) ??
      stringValue(result?.status),
    raceTimeMs:
      numberValue(record.raceTimeMs) ??
      numberValue(result?.timeMs) ??
      (resultTimeSeconds !== null ? resultTimeSeconds * 1000 : null),
    raceGapMs:
      numberValue(record.raceGapMs) ??
      numberValue(result?.gapMs) ??
      (resultGapSeconds !== null ? resultGapSeconds * 1000 : null),
  };
}

export function normalizeRides(raw: unknown): RideRecord[] {
  const record = asRecord(raw);
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(record?.rides)
      ? record.rides
      : Array.isArray(record?.items)
        ? record.items
        : Array.isArray(record?.results)
          ? record.results
          : [];
  return list.map(normalizeRide);
}

function tokenFrom(raw: unknown): string {
  if (typeof raw === "string" && raw.trim() !== "") return raw;
  const record = asRecord(raw);
  const token =
    stringValue(record?.token) ??
    stringValue(record?.wsToken) ??
    stringValue(record?.guestToken) ??
    stringValue(record?.value);
  if (token) return token;
  throw new ApiError(502, "bad_response", "The server did not return a ride token.");
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function errorFrom(response: Response, body: unknown): ApiError {
  const record = asRecord(body);
  const code =
    stringValue(record?.error) ??
    stringValue(record?.code) ??
    stringValue(record?.errorCode) ??
    `http_${response.status}`;
  const message =
    stringValue(record?.message) ??
    (typeof body === "string" ? stringValue(body) : null) ??
    undefined;
  return new ApiError(response.status, code, message);
}

function joinBase(baseUrl: string, path: string): string {
  if (!baseUrl) return path;
  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

/**
 * Same-origin HTTP client. The session cookie rides along automatically; no
 * token is ever stored in JavaScript for the HTTP API.
 */
export class HttpRaceApi implements RaceApi {
  private readonly fetchImpl: typeof fetch;
  private readonly baseUrl: string;

  constructor(options: HttpRaceApiOptions = {}) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.baseUrl = options.baseUrl ?? "";
  }

  async getMe(signal?: AbortSignal): Promise<Me | null> {
    try {
      const body = await this.request("/api/me", { signal });
      return normalizeMe(body);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
        return null;
      }
      throw error;
    }
  }

  async updateProfile(update: ProfileUpdate): Promise<Me> {
    await this.request("/api/profile", {
      method: "PATCH",
      body: JSON.stringify(update),
    });
    // Some servers return only the profile row. A fresh /api/me keeps the user
    // id and category mirror authoritative for the client.
    const me = await this.getMe();
    if (!me) throw new ApiError(401, "unauthorized", "Your session expired while saving.");
    return me;
  }

  async getRaces(signal?: AbortSignal): Promise<RaceSummary[]> {
    const body = await this.request("/api/races", { signal });
    const record = asRecord(body);
    const list = Array.isArray(body)
      ? body
      : Array.isArray(record?.races)
        ? record.races
        : Array.isArray(record?.events)
          ? record.events
          : [];
    return list.map(normalizeRace);
  }

  async getRace(id: string, signal?: AbortSignal): Promise<RaceSummary> {
    const body = await this.request(`/api/races/${encodeURIComponent(id)}`, { signal });
    const record = asRecord(body);
    return normalizeRace(record?.race ?? record?.event ?? body);
  }

  async registerForRace(id: string, category: RaceCategory): Promise<RaceRegistration> {
    const body = await this.request(`/api/races/${encodeURIComponent(id)}/register`, {
      method: "POST",
      body: JSON.stringify({ category }),
    });
    const record = asRecord(body);
    const registration = normalizeRegistration(record?.registration ?? body);
    if (!registration) {
      throw new ApiError(502, "bad_response", "The server did not confirm the registration.");
    }
    return registration;
  }

  async withdrawFromRace(id: string): Promise<void> {
    await this.request(`/api/races/${encodeURIComponent(id)}/register`, {
      method: "DELETE",
    });
  }

  async getRaceResults(id: string, signal?: AbortSignal): Promise<RaceResults> {
    const body = await this.request(`/api/races/${encodeURIComponent(id)}/results`, { signal });
    const record = asRecord(body);
    return normalizeRaceResults(record?.results ?? body);
  }

  async getRides(signal?: AbortSignal): Promise<RideRecord[]> {
    return normalizeRides(await this.request("/api/rides", { signal }));
  }

  async getWsToken(): Promise<string> {
    return tokenFrom(await this.request("/api/ws-token"));
  }

  async getGuestToken(): Promise<string> {
    try {
      return tokenFrom(await this.request("/api/guest-token"));
    } catch (error) {
      // The plan text says POST; the API table says GET. Support both.
      if (error instanceof ApiError && (error.status === 404 || error.status === 405)) {
        return tokenFrom(
          await this.request("/api/guest-token", {
            method: "POST",
            body: JSON.stringify({}),
          }),
        );
      }
      throw error;
    }
  }

  async sendMagicLink(email: string, callbackURL?: string): Promise<void> {
    await this.request("/api/auth/sign-in/magic-link", {
      method: "POST",
      body: JSON.stringify({ email, ...(callbackURL ? { callbackURL } : {}) }),
    });
  }

  async testLogin(email: string): Promise<void> {
    await this.request("/api/test/login", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async isTestLoginAvailable(): Promise<boolean> {
    try {
      const response = await this.fetchImpl(joinBase(this.baseUrl, "/api/test/login"), {
        method: "HEAD",
        credentials: "same-origin",
        cache: "no-store",
      });
      return response.status !== 404 && response.status !== 501;
    } catch {
      return false;
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.request("/api/auth/sign-out", {
        method: "POST",
        body: JSON.stringify({}),
      });
    } catch (error) {
      // Already signed out is fine; a missing endpoint is not.
      if (error instanceof ApiError && error.status === 401) return;
      throw error;
    }
  }

  private async request(path: string, init: RequestInit = {}): Promise<unknown> {
    let response: Response;
    try {
      response = await this.fetchImpl(joinBase(this.baseUrl, path), {
        credentials: "same-origin",
        cache: "no-store",
        ...init,
        headers: {
          ...(init.body ? JSON_HEADERS : {}),
          ...(init.headers ?? {}),
        },
      });
    } catch (error) {
      if (
        typeof DOMException !== "undefined" &&
        error instanceof DOMException &&
        error.name === "AbortError"
      ) {
        throw error;
      }
      throw new ApiError(0, "network_error", ERROR_MESSAGES.network_error);
    }

    const body = await readBody(response);
    if (!response.ok) throw errorFrom(response, body);
    return body;
  }
}

export function createRaceApi(options: HttpRaceApiOptions = {}): RaceApi {
  return new HttpRaceApi(options);
}

/** Default same-origin client used by the app. */
export const raceApi: RaceApi = createRaceApi();
